import {
  type FlowDefinition,
  FlowRunView,
  type NodeExecutorKind,
  type NodeRunMeta,
  type NodeRunStatus,
  type NodeTileChip,
  RunGraphs,
} from "@docit/flow-editor";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import type { RunArtifact, RunTree } from "~/api/opsTypes";
import type { RunStep } from "~/api/types";
import {
  artifactVersionKey,
  metasFor,
  type RunGraphSnapshot,
  runFlowDefinition,
  statusesFor,
  stepTokens,
  takenEdgeKey,
  takenEdgesFor,
} from "~/features/runs/runDerive";
import { Empty } from "~/foundation/components/Empty";

/**
 * Nothing read yet — a module constant rather than a fresh `{}` per render, for the same
 * identity reason as everything else in this file.
 */
const NO_ARTIFACT_CONTENTS = {} as const;

/**
 * Hold a value's identity steady until its CONTENT changes.
 *
 * This is the memoisation discipline the wave record calls load-bearing, expressed once. A
 * `useMemo` cannot do it: its dependency would be the very array whose identity churns. Here
 * the dependency is a joined string of the values, so a poll that returns the same edges or
 * the same artifact versions hands React Flow the same object it already has.
 */
function useStable<T>(value: T, key: string): T {
  const held = useRef<{ key: string; value: T }>({ key, value });
  if (held.current.key !== key) {
    held.current = { key, value };
  }
  return held.current.value;
}

export interface RunCanvasProps {
  mode: "canvas" | "graphs";
  graph: RunGraphSnapshot | null;
  steps: RunStep[];
  artifacts: RunArtifact[];
  currentNodeId: string | null;
  runName: string;
  onReadArtifact: (path: string) => void;
  /**
   * Weave (Docit Work 15 §6.1): what each node IS — the executor kind and the tile it carries.
   * Absent (an ordinary flow, or a server that predates Weave) ⇒ the canvas draws as before.
   */
  tree?: RunTree | null;
  /** Open a tile from its chip on the node card. */
  onOpenTile?: (key: string) => void;
  /**
   * The flow's stored definition (`flows.definition`), when the page could read it — the source of
   * each node's prompt when the run's own graph snapshot does not carry the words.
   */
  definition?: unknown;
}

/**
 * The run drawn as its own graph, or as the per-node measurement charts.
 *
 * The scene above ticks an elapsed clock once a second, so this component re-renders once a
 * second too. In kms, rebuilding `takenEdges` or `artifactVersions` inline handed React Flow a
 * fresh array identity on every tick, which tore down and re-initialised the viewport — the
 * canvas visibly vanished and reappeared about 1×/s. Everything passed downward here goes
 * through {@link useStable} for that reason.
 */
export function RunCanvas({
  mode,
  graph,
  steps,
  artifacts,
  currentNodeId,
  runName,
  onReadArtifact,
  tree = null,
  onOpenTile,
  definition = null,
}: RunCanvasProps) {
  const { t } = useTranslation();

  // Weave: the per-node maps the node card reads. Keyed by the tree's own signature so a poll that
  // changed nothing hands React Flow the objects it already has (the identity discipline above).
  const treeKey = tree
    ? tree.nodes
        .map(
          (node) =>
            `${node.nodeId}:${node.executor}:${node.runStatus}:${node.queuedReason ?? ""}:${node.attempts}:` +
            `${node.tile ? `${node.tile.key}/${node.tile.state}/${node.tile.blocked}/${node.tile.held}` : "-"}`,
        )
        .join("|")
    : "";
  const tiles = useStable<Record<string, NodeTileChip>>(
    tree
      ? Object.fromEntries(
          tree.nodes.flatMap((node) =>
            node.tile
              ? [
                  [
                    node.nodeId,
                    {
                      key: node.tile.key,
                      kind: node.tile.kind,
                      state: node.tile.state,
                      category: node.tile.category,
                      title: node.tile.title,
                      priority: node.tile.priority,
                      severity: node.tile.severity,
                      estimate: node.tile.estimate,
                      blocked: node.tile.blocked,
                      blockedReason: node.tile.blockedReason,
                      held: node.tile.held,
                      assignee: node.tile.assignee,
                      isRef: node.tile.isRef,
                      role: node.tile.role,
                    } satisfies NodeTileChip,
                  ] as const,
                ]
              : [],
          ),
        )
      : {},
    `tiles#${treeKey}`,
  );
  const executors = useStable<Record<string, NodeExecutorKind | string>>(
    tree ? Object.fromEntries(tree.nodes.map((node) => [node.nodeId, node.executor])) : {},
    `exec#${treeKey}`,
  );
  const queuedReasons = useStable<Record<string, string>>(
    tree
      ? Object.fromEntries(
          tree.nodes.flatMap((node) =>
            node.queuedReason ? [[node.nodeId, node.queuedReason] as const] : [],
          ),
        )
      : {},
    `queued#${treeKey}`,
  );
  // Attempts (retries included) — only where the tree counted any; never inferred from statuses.
  const attempts = useStable<Record<string, number>>(
    tree
      ? Object.fromEntries(
          tree.nodes.flatMap((node) =>
            node.attempts > 0 ? [[node.nodeId, node.attempts] as const] : [],
          ),
        )
      : {},
    `attempts#${treeKey}`,
  );

  // The run's graph expressed as a flow definition: transitions carry the edges (that is where the
  // canvas's own `flowToRf` looks for them), and each node carries its definition's words — the
  // cascade pill's prompt. Built every render (cheap), but held by CONTENT so a poll that changed
  // nothing hands React Flow the definition it already has.
  const builtFlow = runFlowDefinition({ runName, graph, tree, definition });
  const flow = useStable<FlowDefinition | null>(
    builtFlow,
    builtFlow ? JSON.stringify(builtFlow) : "none",
  );

  const stepKey = steps
    .map((step) => `${step.nodeId}:${step.status}:${step.durationMs}:${stepTokens(step)}`)
    .join("|");
  // Weave: the tree knows the states the step rows cannot express — waiting on a person, queued
  // behind a cap, held by a human — so where it speaks it wins over the step-derived status.
  const statuses = useStable<Record<string, NodeRunStatus>>(
    {
      ...statusesFor(steps),
      ...Object.fromEntries(
        (tree?.nodes ?? [])
          .filter((node) => ["waiting", "queued", "held", "skipped"].includes(node.runStatus))
          .map((node) => [node.nodeId, node.runStatus as NodeRunStatus]),
      ),
    },
    `${stepKey}#${treeKey}`,
  );
  const metas = useStable<Record<string, NodeRunMeta>>(
    metasFor(steps) as Record<string, NodeRunMeta>,
    stepKey,
  );

  const edges = takenEdgesFor(steps);
  const takenEdges = useStable(edges, takenEdgeKey(edges));

  const versions: Record<string, number> = {};
  for (const artifact of artifacts) {
    versions[artifact.path] = Math.max(versions[artifact.path] ?? 0, artifact.version);
  }
  const artifactVersions = useStable(versions, artifactVersionKey(artifacts));

  if (!flow) {
    return (
      <Empty
        message={t(
          "This run carries no graph snapshot, so there is nothing to draw. Runs started before the server recorded graphs have none.",
        )}
      />
    );
  }

  if (mode === "graphs") {
    return (
      <div className="overflow-x-auto rounded-md border border-divider bg-bg-secondary p-3">
        <RunGraphs flow={flow} metas={metas} />
      </div>
    );
  }

  return (
    // Taller than the classic graph needed: the cascade's pills carry their prompts.
    <div className="h-[36rem] overflow-hidden rounded-md border border-divider">
      <FlowRunView
        flow={flow}
        statuses={statuses}
        currentNodeId={currentNodeId}
        metas={metas}
        takenEdges={takenEdges}
        onReadArtifact={onReadArtifact}
        artifactContents={NO_ARTIFACT_CONTENTS}
        artifactVersions={artifactVersions}
        tiles={tiles}
        executors={executors}
        queuedReasons={queuedReasons}
        onOpenTile={onOpenTile}
        attempts={attempts}
      />
    </div>
  );
}
